<template>
  <div class="best-deals">
    <h2>Best Cruise Deals</h2>

    <div class="deal-page-trust-banner">
      <span class="trust-badge">DATA BASIS</span>
      <p class="trust-text">
        Fares are tracked daily from cruise line booking sources. &quot;Save&quot; amounts compare current fares against our 30-day recorded peak for each cabin and sail date, not cruise line list prices.
      </p>
    </div>

    <DealFilters 
      :filters="filters" 
      @filter-change="handleFilterChange"
    />
    
    <div class="controls-bar">
      <div class="control-group">
        <div class="control-label-row">
          <label for="deal-type">Deal Type:</label>
          <deal-explainer-popover
            title="How Deal Types Work"
            :definitions="dealTypeDefinitions"
            aria-label="Deal type explanation"
          />
        </div>
        <select id="deal-type" v-model="dealType" :disabled="loading" @change="fetchDeals">
          <option value="price">Best Price</option>
          <option value="value">Best Value</option>
          <option value="discount">Biggest Discount</option>
        </select>
      </div>
      
      <div class="control-group">
        <label for="sort-by">Sort By:</label>
        <select id="sort-by" v-model="sortBy" :disabled="loading" @change="fetchDeals">
          <option value="price">Price</option>
          <option value="value">Value</option>
          <option value="discount">Discount</option>
        </select>
      </div>
      
      <div class="control-group">
        <label for="sort-order">Order:</label>
        <select id="sort-order" v-model="sortOrder" :disabled="loading" @change="fetchDeals">
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>
      
      <div class="control-group">
        <label for="per-page">Per Page:</label>
        <select id="per-page" v-model.number="perPage" :disabled="loading" @change="fetchDeals">
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
        </select>
      </div>
    </div>
    
    <div v-if="loading" class="loading-indicator" :class="{ 'loading-indicator--first': firstLoad }">
      <div class="spinner"></div>
      <p>Finding the best deals...</p>
    </div>
    
    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="fetchDeals" class="retry-button">Try Again</button>
    </div>
    
    <EmptyState
      v-else-if="deals.length === 0"
      type="deals"
      message="No deals found. Please try different criteria."
    />
    
    <div v-else>
      <div class="deals-list">
        <div v-for="(deal, index) in deals" :key="deal.id" class="deal-card">
          <div class="deal-rank">{{ (currentPage - 1) * perPage + index + 1 }}</div>
          <div v-if="showBestBadge(deal)" class="best-deal-badge">BEST DEAL</div>
          
          <div class="deal-header">
            <h3 class="cs-title-case">{{ titleCase(deal.name) }}</h3>
            <div class="cruise-line-badge cs-line-pill">
              {{ deal.cruise_line }}
            </div>
          </div>

          <div class="deal-body">
            <p class="deal-subline">
              <span v-if="deal.ship_name">{{ deal.ship_name }}</span>
              <span v-if="deal.ship_name && deal.cruise_line" aria-hidden="true"> · </span>
              <span v-if="deal.cruise_line">{{ deal.cruise_line }}</span>
            </p>
            <p class="deal-meta">
              <template v-if="deal.departure_port">From {{ deal.departure_port }}</template>
              <template v-if="deal.departure_port && deal.duration"> · </template>
              <template v-if="deal.duration">{{ nightsLabel(deal.duration) }}</template>
              <template v-if="deal.departure_port || deal.duration"> · </template>
              {{ formatDate(deal.sail_date || deal.departure_date) }}
            </p>
          </div>
          
          <div class="deal-price">
            <div v-if="hasDealPrice(deal)" class="current-price">
              <span class="price-label">From</span>
              <span class="price-value">${{ formatPrice(deal.current_price) }}</span>
              <span class="price-person">per person</span>
              <span v-if="deal.current_cabin_type" class="cabin-type">({{ deal.current_cabin_type }})</span>
              <span class="price-freshness">{{ priceCue(deal) }}</span>
            </div>
            <div v-else class="current-price">
              <span class="price-unavailable">{{ hiddenPriceReason(deal) }}</span>
              <span v-if="staleAgeStamp(deal)" class="price-freshness">{{ staleAgeStamp(deal) }}</span>
            </div>
            
            <div v-if="hasDealPrice(deal) && deal.discount_percent" class="discount">
              <span class="discount-value">{{ deal.discount_percent }}%</span>
              <span class="discount-label">OFF</span>
            </div>
            
            <div v-if="hasDealPrice(deal) && deal.original_price" class="original-price">
              <span class="price-label">Was</span>
              <span class="price-value">${{ formatPrice(deal.original_price) }}</span>
              <span v-if="deal.peak_cabin_type" class="cabin-type">({{ deal.peak_cabin_type }})</span>
            </div>
            
            <div v-if="hasDealPrice(deal) && deal.value_score" class="value-score">
              <span class="price-label">Per Night</span>
              <span class="price-value">${{ formatPrice(deal.value_score) }}</span>
            </div>
          </div>
          
          <div class="deal-actions">
            <router-link :to="getCruiseUrl(deal.id, deal.sail_date)" class="view-details-button cs-btn-primary" :aria-label="sailingLinkAriaLabel(deal)">{{ sailingLinkText(deal) }}</router-link>
            <button type="button" @click="viewPriceHistory(deal.id, deal.sail_date)" class="view-history-button cs-btn-outline" :aria-label="priceHistoryAriaLabel(deal)">View Price History</button>
            <a v-affiliate-link
              v-if="canBook(deal.cruise_line)"
              :href="affiliateUrl(deal.id)"
              target="_blank"
              rel="noopener noreferrer sponsored"
              class="cs-btn-book-outline view-book-button"
              :aria-label="ctaAriaLabel(deal)"
            >
              {{ tierCta(deal) }} <i class="fas fa-external-link-alt" aria-hidden="true"></i>
            </a>
            <span v-else class="cs-btn-book-disabled view-book-quiet" role="status" title="Direct booking is not available on this site">Not Bookable Here</span>
          </div>
          <p v-if="canBook(deal.cruise_line)" class="deal-affiliate-note">{{ tierDealNote(deal) }}</p>
        </div>
      </div>
      
      <div v-if="pagination && pagination.pages > 1" class="pagination">
        <button 
          @click="goToPage(currentPage - 1)" 
          :disabled="!pagination.has_prev || loading"
          class="page-button"
        >
          Previous
        </button>
        
        <span class="page-info">
          Page {{ pagination.page }} of {{ pagination.pages }} 
          ({{ pagination.total }} total deals)
        </span>
        
        <button 
          @click="goToPage(currentPage + 1)" 
          :disabled="!pagination.has_next || loading"
          class="page-button"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { affiliateState, canBookLine } from '@/utils/affiliateFlag';
import { dealNote, tierCtaLabel } from '@/utils/bookingTierCopy';
import { actionAriaLabel, sailingLinkAriaLabel, sailingLinkText } from '@/utils/sailingLinkLabel';
import DealFilters from '@/components/DealFilters.vue';
import EmptyState from '@/components/EmptyState.vue';
import DealExplainerPopover from '@/components/DealExplainerPopover.vue';
import { hiddenPriceReason, listingPriceCue, showsBestDealBadge, showsListingPrice, staleAgeStamp } from '@/utils/priceFreshness';

import { formatPriceAmount } from '@/utils/formatMoney'
import { formatPlaceTitle } from '@/utils/formatPlaceTitle';
export default {
  components: {
    DealFilters,
    EmptyState,
    DealExplainerPopover
  },
  data() {
    return {
      deals: [],
      pagination: null,
      loading: true,
      // Deals CLS: false until the first fetch has settled (see .loading-indicator--first).
      hasLoaded: false,
      error: null,
      dealType: 'price',
      sortBy: 'price',
      sortOrder: 'asc',
      perPage: 10,
      currentPage: 1,
      filters: {},
      debounceTimer: null,
      abortController: null
    };
  },
  computed: {
    /** Deals CLS: initial load only: loading and no fetch has settled yet (see .loading-indicator--first). */
    firstLoad() {
      return this.loading && !this.hasLoaded && !this.error;
    },
    // CP-142: Best Deals CTAs follow the single runtime flag from /api/config.
    affiliateEnabled() {
      return affiliateState.enabled;
    },
    dealTypeDefinitions() {
      return [
        {
          label: 'Best Value',
          body: 'Calculated by dividing the lowest available cabin fare by voyage length (fare ÷ nights). Ranks cruises that deliver the lowest per-night cost.'
        },
        {
          label: 'Price Drop',
          body: 'We monitor daily fares directly from cruise line listings. When a cabin on a specific sail date drops below its 30-day recorded peak, we flag it as a Price Drop with the dollar and percentage savings.'
        },
        {
          label: 'Biggest Discount',
          body: 'The largest percentage drop from the highest recorded fare for the same cabin type and sail date over the past 30 days.'
        }
      ];
    }
  },
  mounted() {
    this.fetchDeals();
  },
  beforeUnmount() {
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  methods: {
    // CP-212: one pattern for every sailing link (visible text + ship/date accessible name).
    sailingLinkText,
    sailingLinkAriaLabel,
    // CP-212: the CruiseDirect link's accessible name leads with its visible text, then the sailing.
    priceHistoryAriaLabel(deal) {
      return actionAriaLabel('View Price History', deal);
    },
    ctaAriaLabel(deal) {
      return actionAriaLabel(this.tierCta(deal), deal, '(opens in a new tab)');
    },
    // CP-143: ON-state copy from the landing tier (only reached when canBook is true).
    tierCta(deal) {
      return tierCtaLabel(deal && deal.affiliate_landing_tier);
    },
    tierDealNote(deal) {
      return dealNote({
        tier: deal && deal.affiliate_landing_tier,
        line: deal && deal.cruise_line,
        collectedAt: deal && deal.price_updated_at,
      });
    },
    // CP-141: CTA only when the flag is on and CruiseDirect sells the line.
    canBook(line) {
      return canBookLine(this.affiliateEnabled, line);
    },
    handleFilterChange(newFilters) {
      this.filters = newFilters;
      this.currentPage = 1; // Reset to first page when filters change
      this.debounceFilterChange();
    },
    
    debounceFilterChange() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      this.debounceTimer = setTimeout(() => {
        this.fetchDeals();
      }, 300);
    },
    
    async fetchDeals() {
      if (this.abortController) {
        this.abortController.abort();
      }
      
      this.loading = true;
      this.error = null;
      this.abortController = new AbortController();
      
      try {
        const params = {
          type: this.dealType,
          limit: this.perPage,
          page: this.currentPage,
          per_page: this.perPage,
          sort_by: this.sortBy,
          sort_order: this.sortOrder,
          ...this.filters
        };
        
        const response = await apiService.getBestDeals(params, { signal: this.abortController.signal });
        
        // Handle new response format with pagination
        if (response.data.deals) {
          this.deals = response.data.deals;
          this.pagination = response.data.pagination;
        } else {
          // Fallback for old format
          this.deals = Array.isArray(response.data) ? response.data : [];
          this.pagination = null;
        }
        
        this.loading = false;
        this.hasLoaded = true;
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        
        console.error('Error fetching deals:', error);
        this.error = 'Failed to load deals. Please try again later.';
        this.loading = false;
        this.hasLoaded = true;
      }
    },
    
    goToPage(page) {
      if (this.pagination && page >= 1 && page <= this.pagination.pages) {
        this.currentPage = page;
        this.fetchDeals();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    
    viewPriceHistory(cruiseId, sailDate) {
      let url = `/cruise/${cruiseId}`;
      if (sailDate) {
        url += `?sail_date=${encodeURIComponent(sailDate)}`;
      }
      this.$router.push(url);
    },
    
    getCruiseUrl(cruiseId, sailDate) {
      let url = `/cruise/${cruiseId}`;
      if (sailDate) {
        url += `?sail_date=${encodeURIComponent(sailDate)}`;
      }
      return url;
    },
    
    affiliateUrl(cruiseId) {
      return `/api/affiliate/redirect?cruise_id=${cruiseId}&partner=cruisedirect&source=best`;
    },
    
    getCruiseLineClass(cruiseLine) {
      return 'cruise-line-' + cruiseLine.toLowerCase().replace(/\s+/g, '-');
    },
    
    formatPrice(price) {
      return formatPriceAmount(price);
    },
    hasDealPrice(deal) {
      return showsListingPrice(deal);
    },
    showBestBadge(deal) {
      return showsBestDealBadge(deal);
    },
    priceCue(deal) {
      return listingPriceCue(deal);
    },
    staleAgeStamp,
    hiddenPriceReason,
    
    titleCase(str) {
      return formatPlaceTitle(str);
    },
    nightsLabel(n) {
      const num = Number(n);
      if (!Number.isFinite(num) || num <= 0) return '';
      const rounded = Math.round(num);
      return rounded === 1 ? '1 night' : `${rounded} nights`;
    },
    formatDate(dateString) {
      if (!dateString) return 'TBD';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  }
};
</script>

<style scoped>
.best-deals {
  margin: 0;
  padding: 0;
}

h2 {
  text-align: center;
  margin-bottom: var(--space-5);
  color: var(--color-text);
  font-family: var(--font-heading);
}

.deal-page-trust-banner {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: var(--cs-sand);
  border: 1px solid var(--cs-card-border);
  border-left: 3px solid var(--cs-signal-teal);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  margin: var(--space-3) 0 var(--space-4) 0;
}

.trust-badge {
  background: var(--cs-signal-mist);
  color: var(--cs-signal-teal);
  font-family: var(--font-heading);
  font-weight: bold;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.05em;
  padding: 2px 6px;
  border-radius: var(--radius-pill);
  flex-shrink: 0;
}

.trust-text {
  margin: 0;
  font-size: var(--text-small);
  color: var(--cs-ink);
  line-height: var(--line-height-body);
}

.control-label-row {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.control-label-row label {
  margin-bottom: 0;
}

.controls-bar {
  display: flex;
  justify-content: center;
  gap: var(--space-5);
  margin-bottom: var(--space-5);
  padding: var(--space-4);
  background-color: var(--color-surface);
  border-radius: var(--radius-md);
  flex-wrap: wrap;
  box-shadow: var(--shadow-card);
}

.control-group {
  display: flex;
  flex-direction: column;
  min-width: 120px;
}

.control-group label {
  margin-bottom: var(--space-2);
  font-weight: 600;
  color: var(--color-text-muted);
  font-size: var(--text-small);
}

.control-group select {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: var(--text-body);
  background: var(--color-surface);
  color: var(--color-text);
}

.control-group select:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px var(--color-primary-light);
}

.deals-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.deal-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--color-surface);
  box-shadow: var(--shadow-card);
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
}

.deal-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-card-hover);
}

.deal-rank {
  position: absolute;
  top: var(--space-3);
  left: var(--space-3);
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--cs-fill);
  color: white;
  border-radius: 50%;
  font-weight: bold;
  font-size: var(--text-body);
}

.deal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-4) var(--space-4) var(--space-4) 50px;
  background-color: var(--color-background);
  border-bottom: 1px solid var(--color-border-light);
}

.deal-header h3 {
  margin: 0;
  font-size: var(--text-h3);
  color: var(--color-text);
  flex: 1;
}

.cruise-line-badge {
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-pill);
  font-size: var(--text-caption);
  font-weight: bold;
  color: white;
  margin-left: var(--space-3);
}

/* Cruise line specific colors */
.cruise-line-royal-caribbean {
  background-color: #1e88e5;
}

.cruise-line-carnival-cruise-line {
  background-color: #e53935;
}

.cruise-line-norwegian-cruise-line {
  background-color: #43a047;
}

.cruise-line-msc-cruises {
  background-color: #3949ab;
}

.cruise-line-princess-cruises {
  background-color: #8e24aa;
}

.cruise-line-holland-america-line {
  background-color: #00897b;
}

.cruise-line-celebrity-cruises {
  background-color: #5e35b1;
}

.cruise-line-disney-cruise-line {
  background-color: #7b1fa2;
}

.cruise-line-viking-cruises {
  background-color: #d81b60;
}

.cruise-line-cunard-line {
  background-color: #6d4c41;
}

.deal-body {
  padding: 0 var(--space-4) var(--space-3);
}
.deal-subline {
  margin: 0 0 var(--space-1);
  font-size: 13px;
  line-height: 18px;
  color: var(--cs-muted);
}
.deal-meta {
  margin: 0;
  font-size: var(--text-small);
  color: var(--cs-muted);
}
.price-person {
  display: block;
  font-size: var(--text-caption);
  color: var(--color-text-muted);
}
.deal-details {
  padding: var(--space-4);
}

.deal-details p {
  margin: var(--space-2) 0;
  color: var(--color-text-muted);
}

.deal-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-4);
  background-color: var(--color-accent-success-bg);
  flex-wrap: wrap;
  gap: var(--space-3);
}

.current-price {
  display: flex;
  flex-direction: column;
}

.price-label {
  font-size: var(--text-small);
  color: var(--color-text-muted);
}

.price-value {
  font-size: var(--text-h2);
  font-weight: bold;
  color: var(--color-accent-success);
}

.discount {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: var(--cs-deal-accent);
  color: white;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
}

.discount-value {
  font-size: var(--text-h3);
  font-weight: bold;
}

.discount-label {
  font-size: var(--text-caption);
}

.original-price {
  display: flex;
  flex-direction: column;
  text-align: right;
}

.original-price .price-value {
  color: var(--color-text-muted);
  text-decoration: line-through;
  font-size: var(--text-h3);
}

.value-score {
  display: flex;
  flex-direction: column;
  text-align: right;
}

.cabin-type {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  font-weight: normal;
  font-style: italic;
  margin-left: 0.3rem;
}

.deal-actions {
  display: flex;
  flex-wrap: wrap;
  padding: var(--space-4);
  gap: var(--space-3);
}

@media (max-width: 390px) {
  .deal-actions {
    flex-direction: column;
  }
}

.view-book-button {
  flex: 1;
}

.view-book-quiet {
  flex: 0 0 100%;
  margin-top: 2px;
}

.deal-affiliate-note {
  margin: calc(-1 * var(--space-2)) 0 0;
  padding: 0 var(--space-4) var(--space-4);
  font-size: 11px;
  color: var(--cs-muted);
  line-height: 1.4;
}

.view-history-button, .view-details-button {
  flex: 1;
  padding: var(--space-3) var(--space-4);
  text-align: center;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 600;
  text-decoration: none;
  transition: background var(--transition-normal);
}

.view-history-button {
  background-color: var(--cs-fill);
  color: white;
  border: none;
}

.view-history-button:hover {
  background-color: var(--cs-fill-hover);
}

.view-details-button {
  background-color: var(--color-secondary);
  color: white;
  border: none;
  display: inline-block;
}

.view-details-button:hover {
  background-color: var(--color-secondary-hover);
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--space-5);
  margin-top: var(--space-5);
  padding: var(--space-5);
}

.page-button {
  padding: var(--space-3) var(--space-5);
  background-color: var(--cs-fill);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 600;
  transition: background var(--transition-normal);
}

.page-button:hover:not(:disabled) {
  background-color: var(--cs-fill-hover);
}

.page-button:disabled {
  background-color: var(--color-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

.page-info {
  color: var(--color-text-muted);
  font-weight: 600;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: var(--space-6) 0;
}

/* Deals CLS: desktop CLS, same trick as CP-230 on Browse. While the first fetch is in flight the page
   is only about one screen tall, so footer.main-footer sat inside the viewport (taller windows
   show more of it) and jumped down thousands of px when the cards rendered (CLS 0.037 at 1350x940,
   0.056-0.059 at 1920x1080 / 1280x1024). The loader is now taller than the viewport, so the footer
   starts below the fold and stays there until the list arrives. The spinner stays at the top. The
   box exists only under v-if="loading" and only until the first fetch settles (hasLoaded), so a
   short or empty result takes its natural height afterwards (no standing gap) and a refetch from
   page / sort / filter keeps the short loader (a 100vh box there would itself cause a jump). */
.best-deals > .loading-indicator--first {
  min-height: 100vh;
  min-height: 100svh;
  justify-content: flex-start;
}

.loading-indicator p {
  color: var(--color-text-muted);
  margin-top: var(--space-4);
}

.spinner {
  border: 4px solid var(--color-border-light);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border-top-color: var(--color-primary);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  text-align: center;
  padding: var(--space-5);
  color: var(--color-error);
  background: var(--color-error-bg);
  border-radius: var(--radius-md);
}

.retry-button {
  margin-top: var(--space-4);
  padding: var(--space-2) var(--space-4);
  background-color: var(--color-secondary);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 600;
  transition: background var(--transition-normal);
}

.retry-button:hover {
  background-color: var(--color-secondary-hover);
}

select:disabled {
  background-color: var(--color-border-light);
  cursor: not-allowed;
  opacity: 0.8;
}

.best-deal-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: var(--cs-amber);
  color: var(--cs-on-amber);
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  letter-spacing: 0.03em;
  z-index: 1;
}

.deal-card {
  position: relative;
  border-radius: var(--radius-lg);
  border: 1px solid var(--cs-card-border);
  padding-top: var(--space-5);
}

.deal-header h3 {
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  padding-right: 6rem;
}

.cruise-line-badge {
  background: var(--cs-signal-mist) !important;
  color: var(--cs-signal-teal) !important;
}

.price-value { color: var(--cs-price); }

.view-details-button {
  background-color: var(--cs-fill) !important;
  min-height: var(--touch-min);
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}

.view-history-button {
  background-color: transparent !important;
  color: var(--cs-signal-teal) !important;
  border: 1.5px solid var(--cs-signal-teal) !important;
  min-height: var(--touch-min);
}

.retry-button {
  min-height: var(--touch-min);
  background-color: var(--cs-fill);
  padding: var(--space-3) var(--space-5);
}

.deals-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 768px) {
  .deals-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .deals-list {
    grid-template-columns: 1fr;
  }
}

.controls-bar {
  flex-direction: column;
  align-items: stretch;
}

.control-group {
  width: 100%;
}

.control-group select {
  min-height: var(--touch-min);
  width: 100%;
}

@media (min-width: 768px) {
  .controls-bar {
    flex-direction: row;
    flex-wrap: wrap;
  }
  .control-group {
    width: auto;
    min-width: 120px;
  }
}

.price-freshness {
  display: block;
  margin-top: var(--space-2);
  font-size: var(--text-caption);
  line-height: 1.35;
  color: var(--color-text-muted);
}

.price-unavailable {
  color: var(--color-text-muted);
  font-style: italic;
  font-size: var(--text-small);
  font-weight: 600;
}

</style>
